/**
 * Shared wall shape-handle edit logic (selection grips + hover drag control).
 *
 * Both `WallShapeHandleLayer` (single selected wall, 3D) and
 * `WallHoverControlLayer` (hover drag control, plan + 3D) drive the SAME
 * gesture pipeline: drag frames rebuild the in-progress geometryData from a
 * pristine snapshot through these pure builders and publish it to the tiny
 * `wallEditPreviewStore`; on release the final target rebuilds through the
 * SAME builder and commits ONCE through the kernel (single undo step), so
 * the committed geometry always equals the last previewed frame (zero pop).
 *
 * Everything here is pure model-space math (plan meters) or a store commit,
 * plus the ONE shared pointer-target resolver both drag gestures use — no
 * React, no three.js scene objects.
 */
import * as THREE from 'three';
import { useBIMStore } from '../state/useBIMStore';
import { WallElement, WallGeometryData } from '../core/types';
import { WallJoinSolver } from '../core/WallJoinSolver';
import { processWallJoins } from '../tools/WallTool';
import { constrainDraftPoint } from '../tools/snapping';

/** Handle / guide palette (shared with the selection shape handles). */
export const HANDLE_COLOR = '#00aaff';
export const GUIDE_COLOR = '#00aaff';
/** Alignment-lock feedback color (axis lock, junction grip). */
export const SNAP_COLOR = '#ff9900';
export const HANDLE_SIZE = 0.09;

/** Shortest run an endpoint drag may leave behind (meters). */
export const WALL_MIN_RUN = 0.05;

/** Fraction of the run counted as an end zone for the hover control —
 *  hovering inside it relocates the control onto that end. */
export const WALL_END_ZONE_FRACTION = 0.2;

export type WallHandleKind = 'start' | 'end' | 'top';

/** Hover-control station on the run (Revit-style mid / left end / right end). */
export type WallStation = 'start' | 'mid' | 'end';

/** One handle move resolved against the ground/wall plane. */
export interface DragTarget {
    /** Endpoint drags: the rounded new start/end (plan meters, z = 0). */
    endpoint?: [number, number, number];
    /** Top drags: the rounded new wall height (meters). */
    height?: number;
    /** Top drags: world Z of the active elevation alignment (snap engaged). */
    snappedZ?: number;
    /** Endpoint drags: endpoint locked onto the wall's own axis line. */
    axisSnapped?: boolean;
}

/** Own-axis extension guide shown during endpoint drags. */
export interface AxisGuideState {
    points: [THREEVector3Tuple, THREEVector3Tuple];
    snapped: boolean;
}

/** Horizontal alignment guide shown while a top-drag snap is engaged. */
export interface HeightGuideState {
    z: number;
    from: THREEVector3Tuple;
    to: THREEVector3Tuple;
}

type THREEVector3Tuple = [number, number, number];

/**
 * Rotate+scale a world-XY plan polygon about a pivot (similarity map). This
 * is the LIVE approximation of what the join solver will produce on release:
 * miters stay visually attached while the run stretches. Cross-section
 * thickness scales by the run factor too — a deliberate trade accepted for
 * the drag only, the committed geometry comes from the exact solver.
 */
function transformPlanPolygon(
    points: [number, number][],
    pivot: { x: number; y: number },
    angle: number,
    scale: number
): [number, number][] {
    const cos = Math.cos(angle) * scale;
    const sin = Math.sin(angle) * scale;
    return points.map(([x, y]) => {
        const dx = x - pivot.x;
        const dy = y - pivot.y;
        return [pivot.x + dx * cos - dy * sin, pivot.y + dx * sin + dy * cos] as [number, number];
    });
}

/**
 * Pure preview builder: apply ONE handle move to the ORIGINAL geometry.
 * Both the live frames and the final commit call it with the same snapshot
 * and the same rounded target, so the committed geometry equals the last
 * previewed frame (zero pop at release).
 */
export function buildPreviewGeometry(
    orig: WallGeometryData,
    kind: WallHandleKind,
    target: DragTarget
): WallGeometryData {
    if (kind === 'top') {
        const oldHeight = orig.height ?? 3;
        const newHeight = target.height ?? oldHeight;
        // Sketched shape profiles scale vertically with the new height so the
        // body keeps following the handle.
        const scaledProfile = Array.isArray(orig.elevationProfile) && oldHeight > 1e-6
            ? orig.elevationProfile.map(([u, v]) => [u, v * (newHeight / oldHeight)] as [number, number])
            : orig.elevationProfile;
        return { ...orig, height: newHeight, topConstraintId: 'Unconnected', elevationProfile: scaledProfile };
    }

    const isStart = kind === 'start';
    const fixed: [number, number] = isStart ? [orig.end[0], orig.end[1]] : [orig.start[0], orig.start[1]];
    const moved: [number, number] = isStart ? [orig.start[0], orig.start[1]] : [orig.end[0], orig.end[1]];
    const next = target.endpoint!;
    const oldLen = Math.hypot(moved[0] - fixed[0], moved[1] - fixed[1]);
    const newLen = Math.hypot(next[0] - fixed[0], next[1] - fixed[1]);
    const updates: WallGeometryData = isStart
        ? { ...orig, start: [next[0], next[1], 0] }
        : { ...orig, end: [next[0], next[1], 0] };

    if (oldLen > 1e-6 && newLen > 1e-6) {
        const angle = Math.atan2(next[1] - fixed[1], next[0] - fixed[0])
            - Math.atan2(moved[1] - fixed[1], moved[0] - fixed[0]);
        const scale = newLen / oldLen;
        const pivot = { x: fixed[0], y: fixed[1] };
        if (Array.isArray(orig.layerProfiles)) {
            updates.layerProfiles = orig.layerProfiles.map((poly) => transformPlanPolygon(poly, pivot, angle, scale));
        }
        if (Array.isArray(orig.profile)) {
            updates.profile = transformPlanPolygon(orig.profile, pivot, angle, scale);
        }
        // Shape profiles live in run-local u meters: rescale to the new run.
        if (Array.isArray(orig.elevationProfile)) {
            updates.elevationProfile = orig.elevationProfile.map(([u, v]) => [u * scale, v] as [number, number]);
        }
    }
    return updates;
}

/**
 * Mid-station slide: translate the whole run (both endpoints and every plan
 * profile) along its own axis by `delta` (already mm-rounded by the caller).
 * The run-local elevation profile travels with the wall unchanged.
 */
export function buildSlideGeometry(orig: WallGeometryData, delta: { x: number; y: number }): WallGeometryData {
    const move = (p: [number, number]) => [p[0] + delta.x, p[1] + delta.y] as [number, number];
    const updates: WallGeometryData = {
        ...orig,
        start: [orig.start[0] + delta.x, orig.start[1] + delta.y, orig.start[2] ?? 0],
        end: [orig.end[0] + delta.x, orig.end[1] + delta.y, orig.end[2] ?? 0],
    };
    if (Array.isArray(orig.layerProfiles)) updates.layerProfiles = orig.layerProfiles.map((poly) => poly.map(move));
    if (Array.isArray(orig.profile)) updates.profile = orig.profile.map(move);
    return updates;
}

/**
 * Which hover-control station the plan point sits in: inside the end zones
 * the control relocates onto that end, otherwise it sits at the midpoint
 * (Revit's mid / left end / right end relocation).
 */
export function resolveWallStation(
    g: Pick<WallGeometryData, 'start' | 'end'>,
    x: number,
    y: number
): WallStation {
    const dx = g.end[0] - g.start[0];
    const dy = g.end[1] - g.start[1];
    const lenSq = dx * dx + dy * dy;
    if (lenSq < 1e-12) return 'mid';
    const t = ((x - g.start[0]) * dx + (y - g.start[1]) * dy) / lenSq;
    if (t <= WALL_END_ZONE_FRACTION) return 'start';
    if (t >= 1 - WALL_END_ZONE_FRACTION) return 'end';
    return 'mid';
}

/** Extent of the height alignment guide: the wall's plan run extended a
 *  little past both ends, drawn at the candidate elevation. */
export function heightGuideExtent(g: WallGeometryData, z: number): HeightGuideState {
    const dx = g.end[0] - g.start[0];
    const dy = g.end[1] - g.start[1];
    const len = Math.hypot(dx, dy) || 1;
    const ext = Math.max(0.4, len * 0.25);
    const z0 = z + 0.01;
    return {
        z,
        from: [g.start[0] - (dx / len) * ext, g.start[1] - (dy / len) * ext, z0],
        to: [g.end[0] + (dx / len) * ext, g.end[1] + (dy / len) * ext, z0],
    };
}

/**
 * Re-solve join profiles with connected walls after an endpoint drag (or a
 * mid-slide), using the same pipeline as the placement flow
 * (processWallJoins + WallJoinSolver).
 */
export function recomputeWallJoins(wallId: string): void {
    const bim = useBIMStore.getState();
    const wall = bim.project.elements.get(wallId) as WallElement | undefined;
    if (!wall) return;
    const neighbors = Array.from(bim.project.elements.values())
        .filter(e => e.category === 'Walls' && e.id !== wallId) as WallElement[];
    const joinStyle = wall.geometryData.joinConfiguration?.startJoinType ?? 'Miter';
    const joins = processWallJoins(wall, neighbors, joinStyle === 'None' ? 'Butt' : joinStyle);
    const connected = neighbors.filter(w => joins.some(j => j.targetId === w.id));
    if (connected.length === 0) return;
    const g = wall.geometryData;
    bim.updateElement(wallId, {
        constraints: joins.map(j => ({ ...j })),
        geometryData: {
            ...g,
            profile: WallJoinSolver.computeWallProfile(wall, connected),
            layerProfiles: WallJoinSolver.computeLayerProfiles(wall, connected),
        },
    } as any);
}

// ------------------------------------------------- endpoint drag resolution

/** Snap tolerance for handle drags: scales with camera distance (1.2%) so
 *  the snap feel stays constant across zoom levels (clamped 0.03–0.6 m). */
export function dragSnapTolerance(camera: THREE.Camera, wallMid: THREE.Vector3): number {
    return Math.min(0.6, Math.max(0.03, camera.position.distanceTo(wallMid) * 0.012));
}

/**
 * Junction OSNAP for endpoint drags: the nearest OTHER wall's defining
 * endpoint (plan distance) within `tol`, returned as exact coordinates.
 * Null when nothing is close. Landing exactly on a neighbor endpoint keeps
 * the junction weld (`JUNCTION_WELD_TOL`) trivially satisfied, so the
 * follow-up carry re-solves the chain.
 */
export function snapToWallEndpoint(
    x: number,
    y: number,
    excludeWallId: string,
    tol: number
): { x: number; y: number } | null {
    const walls: WallElement[] = [];
    useBIMStore.getState().project.elements.forEach((el) => {
        if (el.category === 'Walls' && el.id !== excludeWallId) walls.push(el as WallElement);
    });
    let best: { x: number; y: number } | null = null;
    let bestDist = tol;
    for (const w of walls) {
        const g = w.geometryData;
        if (!g) continue;
        for (const pt of [g.start, g.end]) {
            const d = Math.hypot(pt[0] - x, pt[1] - y);
            if (d <= bestDist) {
                bestDist = d;
                best = { x: pt[0], y: pt[1] };
            }
        }
    }
    return best;
}

/** One endpoint-drag frame's resolved target (both drag gestures). */
export interface EndpointDragResolution {
    endpoint: [number, number, number];
    /** Locked onto the wall's own axis line (direction kept, length only). */
    axisSnapped: boolean;
    /** Locked exactly onto another wall's defining endpoint. */
    endpointSnapped: boolean;
}

/**
 * Shared endpoint-drag target resolution (selection grips + hover control):
 * the raw ground-plane hit runs through the full drafting snap pipeline
 * (grid / polar / OSNAP / ortho), then two BIM-specific locks may override
 * it — another wall's defining endpoint (junction OSNAP, exact) and the
 * wall's OWN axis line ("the path it continues along", pure length change).
 * Free 2D re-angling stays available: the locks engage only within
 * tolerance. The endpoint can never come closer than WALL_MIN_RUN to the
 * fixed end (no degenerate walls), and every path is mm-rounded so preview
 * and commit share one target (zero pop).
 */
export function resolveEndpointDragTarget(
    raw: THREE.Vector3,
    origGeom: WallGeometryData,
    kind: 'start' | 'end',
    camera: THREE.Camera,
    gl: THREE.WebGLRenderer,
    snapTol: number,
    excludeWallId: string
): EndpointDragResolution {
    const isStart = kind === 'start';
    const fixed = isStart
        ? { x: origGeom.end[0], y: origGeom.end[1] }
        : { x: origGeom.start[0], y: origGeom.start[1] };
    const moved = isStart
        ? { x: origGeom.start[0], y: origGeom.start[1] }
        : { x: origGeom.end[0], y: origGeom.end[1] };
    const runLen = Math.hypot(moved.x - fixed.x, moved.y - fixed.y);

    // Full drafting pipeline (grid/polar/OSNAP/ortho) — as the grips do.
    const snapped = constrainDraftPoint(raw, camera, gl, null);
    let x = snapped.x;
    let y = snapped.y;
    let axisSnapped = false;
    let endpointSnapped = false;

    // 1) Junction OSNAP: another wall's defining endpoint wins (exact).
    const endSnap = snapToWallEndpoint(x, y, excludeWallId, snapTol);
    if (endSnap) {
        x = endSnap.x;
        y = endSnap.y;
        endpointSnapped = true;
    } else if (runLen > 1e-6) {
        // 2) Own-axis lock: close to the run's line the endpoint snaps onto
        // it — pure length change, direction kept; off the line the drag
        // stays free 2D (the wall may re-angle).
        const dir = { x: (moved.x - fixed.x) / runLen, y: (moved.y - fixed.y) / runLen };
        const t = (x - fixed.x) * dir.x + (y - fixed.y) * dir.y;
        const px = fixed.x + t * dir.x;
        const py = fixed.y + t * dir.y;
        if (Math.hypot(x - px, y - py) <= snapTol) {
            x = px;
            y = py;
            axisSnapped = true;
        }
    }

    // 3) Degenerate-run guard: keep a minimum length from the fixed end.
    let dx = x - fixed.x;
    let dy = y - fixed.y;
    const len = Math.hypot(dx, dy);
    if (len < WALL_MIN_RUN) {
        if (len > 1e-9) {
            dx /= len;
            dy /= len;
        } else if (runLen > 1e-6) {
            dx = (moved.x - fixed.x) / runLen;
            dy = (moved.y - fixed.y) / runLen;
        } else {
            dx = 1;
            dy = 0;
        }
        x = fixed.x + dx * WALL_MIN_RUN;
        y = fixed.y + dy * WALL_MIN_RUN;
        endpointSnapped = false;
    }

    const round = (v: number) => Math.round(v * 1000) / 1000;
    return { endpoint: [round(x), round(y), 0], axisSnapped, endpointSnapped };
}
